import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BoardResponse, BoardColumn, TaskCard } from '../services/api/board';
import {
  patchTaskState,
  patchTaskStateByName,
  patchTaskVisibility,
} from '../services/api/board';
import { taskActivityQueryKey } from './useTaskActivity';
import { boardQueryKey } from './boardQueryKey';

export type TaskMutationType = 'state' | 'visibility';

export interface TaskMutationInput {
  type: TaskMutationType;
  taskId: number;
  projectId: string;
  audience: 'internal' | 'external';
  toStateId?: number; // required when type === 'state' (unless toStateName)
  /**
   * MKS-419 — merged board: move to the column with this NAME in the task's
   * own project (the server resolves it). Used instead of toStateId when a
   * subproject card is dropped on a merged column.
   */
  toStateName?: string;
  /** MKS-419 — the optimistic update targets the merged board's cache entry. */
  includeSubprojects?: boolean;
  toIsExternal?: boolean; // required when type === 'visibility'
}

interface MutationContext {
  previous: BoardResponse | undefined;
}

/**
 * Phase 24 Plan 24-03 — single mutation hook covering PATCH /state and
 * PATCH /visibility. Optimistic update + rollback on failure (D-06 / D-09).
 *
 * Cache key: ['board', projectId, audience] (D-12). Snapshot taken in
 * onMutate; restored in onError; invalidated in onSettled to reconcile with
 * server (handles last-write-wins concurrent edits).
 *
 * Zero-arg hook — TanStack Query v5 fires both global + per-mutate onError
 * callbacks on failure. Do NOT register a global onError here (would dupe
 * toasts). Caller passes per-mutate { onError } for type-specific toast wording.
 */
export function useTaskMutation() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, TaskMutationInput, MutationContext>({
    mutationFn: (input) => {
      if (input.type === 'state') {
        if (input.toStateName !== undefined) {
          return patchTaskStateByName(input.taskId, input.toStateName);
        }
        if (input.toStateId === undefined) {
          throw new Error('toStateId is required when type === "state"');
        }
        return patchTaskState(input.taskId, input.toStateId);
      }
      if (input.toIsExternal === undefined) {
        throw new Error('toIsExternal is required when type === "visibility"');
      }
      return patchTaskVisibility(input.taskId, input.toIsExternal);
    },
    onMutate: async (input) => {
      const key = boardQueryKey(input.projectId, input.audience, input.includeSubprojects);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BoardResponse>(key);
      if (previous) {
        const optimistic = applyOptimistic(previous, input);
        queryClient.setQueryData<BoardResponse>(key, optimistic);
      }
      return { previous };
    },
    onError: (_err, vars, context) => {
      // Restore the snapshot taken in onMutate; toast surfacing is the caller's
      // responsibility via per-mutate { onError } option (see Plan 24-05).
      if (context?.previous !== undefined) {
        queryClient.setQueryData(
          boardQueryKey(vars.projectId, vars.audience, vars.includeSubprojects),
          context.previous,
        );
      }
    },
    onSettled: (_data, _err, input) => {
      // The 3-element prefix also covers the merged board's 4-element key.
      queryClient.invalidateQueries({
        queryKey: ['board', input.projectId, input.audience],
      });
      queryClient.invalidateQueries({
        queryKey: taskActivityQueryKey(input.taskId),
      });
    },
  });
}

// -----------------------------------------------------------------------------
// Optimistic patch helpers — pure functions, exported for unit testing.
// -----------------------------------------------------------------------------

export function applyOptimistic(board: BoardResponse, input: TaskMutationInput): BoardResponse {
  if (input.type === 'state') {
    if (input.toStateName !== undefined) {
      return moveTaskInBoardByName(board, input.taskId, input.toStateName);
    }
    return moveTaskInBoard(board, input.taskId, input.toStateId!);
  }
  return patchVisibilityInBoard(board, input.taskId, input.toIsExternal!);
}

function moveTaskInBoard(
  board: BoardResponse,
  taskId: number,
  toStateId: number,
): BoardResponse {
  let movedTask: TaskCard | undefined;
  // 1) Remove from source column.
  const withoutTask: BoardColumn[] = board.columns.map((col) => {
    const idx = col.tasks.findIndex((t) => t.id === taskId);
    if (idx === -1) return col;
    movedTask = col.tasks[idx];
    return { ...col, tasks: col.tasks.filter((t) => t.id !== taskId) };
  });
  if (!movedTask) return board; // task not found — leave cache as-is
  // 2) Insert into target column with updated taskStateId; KAN-05: append (createdAt order is stable).
  const next: BoardColumn[] = withoutTask.map((col) =>
    col.taskStateId === toStateId
      ? { ...col, tasks: [...col.tasks, { ...movedTask!, taskStateId: toStateId }] }
      : col,
  );
  // MKS-419 — spread the board so a merged board keeps its `subprojects`.
  return { ...board, columns: next };
}

/**
 * MKS-419 — optimistic move by column NAME (merged board). The card lands in the
 * first non-triage column whose trimmed, lower-cased name matches and KEEPS its
 * own taskStateId: the real state id in the task's own project is not known
 * client-side, and the 5s refetch reconciles it. No matching column → cache
 * left as-is.
 */
function moveTaskInBoardByName(
  board: BoardResponse,
  taskId: number,
  toStateName: string,
): BoardResponse {
  const wanted = toStateName.trim().toLowerCase();
  const targetIdx = board.columns.findIndex(
    (c) => !c.isTriage && c.name.trim().toLowerCase() === wanted,
  );
  if (targetIdx === -1) return board;
  let movedTask: TaskCard | undefined;
  const withoutTask: BoardColumn[] = board.columns.map((col) => {
    const idx = col.tasks.findIndex((t) => t.id === taskId);
    if (idx === -1) return col;
    movedTask = col.tasks[idx];
    return { ...col, tasks: col.tasks.filter((t) => t.id !== taskId) };
  });
  if (!movedTask) return board;
  const next: BoardColumn[] = withoutTask.map((col, i) =>
    i === targetIdx ? { ...col, tasks: [...col.tasks, movedTask!] } : col,
  );
  return { ...board, columns: next };
}

function patchVisibilityInBoard(
  board: BoardResponse,
  taskId: number,
  isExternal: boolean,
): BoardResponse {
  const next: BoardColumn[] = board.columns.map((col) => ({
    ...col,
    tasks: col.tasks.map((t) => (t.id === taskId ? { ...t, isExternal } : t)),
  }));
  return { ...board, columns: next };
}
